import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { MessageSquare, ArrowRight, ArrowLeft, Sparkles, Loader2 } from 'lucide-react';
import { useAgent } from '../../context/AgentContext';
import Button from '../../components/Button';
import { behaviorTemplates } from '../../data/dummyData';
import api from '../../services/api';

export default function StepBehavior() {
  const { goNext, goPrev } = useOutletContext();
  const { currentAgent, updateAgentBehavior, isLoading, error, clearError } = useAgent();

  const [behavior, setBehavior] = useState(currentAgent?.behavior || '');
  const [isFormatting, setIsFormatting] = useState(false);

  const isValid = behavior && behavior.length > 20;

  const applyTemplate = (template) => {
    setBehavior(template.prompt);
    if (error) clearError();
  };

  const handleFormatWithAI = async () => {
    if (!behavior.trim() || isFormatting) return;

    setIsFormatting(true);
    try {
      const response = await api.formatWithAI(
        behavior,
        'behavior_prompt',
        {
          agent_name: currentAgent?.name,
          business_type: currentAgent?.business_type,
        }
      );
      if (response.formatted_text) {
        setBehavior(response.formatted_text);
      }
    } catch (err) {
      console.error('Failed to format with AI:', err);
    } finally {
      setIsFormatting(false);
    }
  };

  const handleContinue = async () => {
    if (!isValid) return;

    try {
      await updateAgentBehavior({ behavior });
      goNext();
    } catch (err) {
      console.error('Failed to save behavior:', err);
    }
  };

  return (
    <div className="p-8 max-w-2xl mx-auto animate-fade-in">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-primary to-accent">
            <MessageSquare className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-text-primary">Define Behavior</h1>
            <p className="text-text-secondary">How should your AI agent communicate?</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Templates */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-text-primary mb-3 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-accent" />
          Quick Templates
        </h3>
        <div className="flex flex-wrap gap-2">
          {behaviorTemplates.map((template) => {
            const Icon = template.icon;
            return (
              <button
                key={template.id}
                onClick={() => applyTemplate(template)}
                disabled={isLoading}
                className={`
                  inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium
                  border transition-all duration-200
                  ${behavior === template.prompt
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border bg-white text-text-secondary hover:border-primary/30 hover:text-primary'
                  }
                  disabled:opacity-50 disabled:cursor-not-allowed
                `}
              >
                {Icon && <Icon className="w-4 h-4 flex-shrink-0" />}
                {template.name}
              </button>
            );
          })}
        </div>
      </div>

      <div className="card p-6">
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-text-primary">
              Behavior Prompt
            </label>
            <button
              type="button"
              onClick={handleFormatWithAI}
              disabled={isLoading || isFormatting || !behavior.trim()}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary bg-primary/10 rounded-lg hover:bg-primary/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isFormatting ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Sparkles className="w-3.5 h-3.5" />
              )}
              {isFormatting ? 'Formatting...' : 'Format with AI'}
            </button>
          </div>
          <textarea
            placeholder="Describe how your AI should behave. For example: 'You are a friendly and helpful sales assistant. Be warm and professional. Always try to understand customer needs before making recommendations...'"
            value={behavior}
            onChange={(e) => {
              setBehavior(e.target.value);
              if (error) clearError();
            }}
            rows={8}
            disabled={isLoading || isFormatting}
            className="input-field resize-none disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <p className="mt-1.5 text-sm text-text-muted">
            This prompt guides your AI's personality and responses. Be specific about tone, knowledge areas, and interaction style.
          </p>
        </div>

        {/* Character count */}
        <div className="mt-2 text-right">
          <span className={`text-sm ${behavior.length < 20 ? 'text-text-muted' : 'text-success'}`}>
            {behavior.length} characters
          </span>
        </div>
      </div>

      {/* Tips */}
      <div className="mt-6 p-4 bg-primary/5 rounded-xl border border-primary/10">
        <h4 className="text-sm font-medium text-primary mb-2">Tips for great behavior prompts</h4>
        <ul className="text-sm text-text-secondary space-y-1">
          <li>Define the personality (friendly, professional, casual)</li>
          <li>Specify what topics the AI should know about</li>
          <li>Include any policies or guidelines to follow</li>
          <li>Mention when to escalate to a human</li>
        </ul>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between mt-8">
        <Button
          variant="ghost"
          icon={ArrowLeft}
          onClick={goPrev}
          disabled={isLoading}
        >
          Back
        </Button>
        <Button
          icon={isLoading ? Loader2 : ArrowRight}
          iconPosition="right"
          onClick={handleContinue}
          disabled={!isValid || isLoading}
          className={isLoading ? '[&_svg]:animate-spin' : ''}
        >
          {isLoading ? 'Saving...' : 'Continue'}
        </Button>
      </div>
    </div>
  );
}